import { useCallback } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { type ContextResolver, useRecommendations } from '@atlassian/app-recommendations-kit';
import { useSubProduct } from '@atlassian/jira-subproduct/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { RecommendationError } from '@atlassian/cross-join-nudges';

import { EntityList, type EntityId } from '../dynamic-messaging/index.tsx';

const CROSS_JOIN_NUDGE_RECOMMENDATIONS_USE_CASE = 'crossJoinNudge';
const MSR_CROSS_JOIN_NUDGE_RECOMMENDATIONS_USE_CASE = 'crossJoinNudgeFlags';

export const RECOMMENDATION_PRODUCT_KEYS = {
	JPD: 'cjn-jira-jpd',
	CONFLUENCE: 'cjn-jira-conf-v2',
} as const;

export interface useNudgeRecommendationsProps {
	sessionId: string;
}

export function useNudgeRecommendations({ sessionId }: useNudgeRecommendationsProps) {
	const cloudId = useCloudId();
	const subProduct = useSubProduct();

	const contextResolver: ContextResolver = useCallback(
		() => ({
			product: 'jira',
			useCase: CROSS_JOIN_NUDGE_RECOMMENDATIONS_USE_CASE,
			tenantId: cloudId,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			subproduct: subProduct as string,
			sessionId,
		}),
		[cloudId, subProduct, sessionId],
	);

	const { getRecommendations } = useRecommendations({ contextResolver });

	return useCallback(async () => {
		try {
			const result = await getRecommendations(1, asEntityIdList(EntityList));

			const { response: { data } = {} } = result;

			// This type assertion is necessary because useRecommendations don't support generic entityId
			const recommendedEntityId =
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				(data && data.length > 0 && (data[0].entityId as EntityId)) || undefined;

			return recommendedEntityId;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					id: 'recommendationFetchFailed',
					packageName: 'jiraCrossJoinNudges',
					teamName: 'groot-growth',
				},
				error,
			});
			throw new Error('Get nudge recommendations error');
		}
	}, [getRecommendations]);
}

export function useMSRNudgeRecommendations({ sessionId }: useNudgeRecommendationsProps) {
	const cloudId = useCloudId();
	const subProduct = useSubProduct();

	const contextResolver: ContextResolver = useCallback(
		() => ({
			product: 'jira',
			useCase: MSR_CROSS_JOIN_NUDGE_RECOMMENDATIONS_USE_CASE,
			tenantId: cloudId,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			subproduct: subProduct as string,
			sessionId,
		}),
		[cloudId, subProduct, sessionId],
	);

	const { getRecommendations } = useRecommendations({ contextResolver });

	return useCallback(async (): Promise<string[]> => {
		try {
			const result = await getRecommendations({
				first: 4,
				config: {
					cacheConfig: {
						ttl: 86400, // cache for 1 day in seconds
					},
				},
			});

			const { response: { data } = {} } = result;

			const recommendedEntities = (data ?? []).map((e) => e.entityId);

			return recommendedEntities;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			let cause;
			if (error instanceof Error) {
				cause = error;
			} else if (error && error.reason) {
				cause = new Error(error.reason);
			} else {
				cause = new Error(String(error));
			}

			throw new RecommendationError('Get MSR nudge recommendations error', {
				cause,
			});
		}
	}, [getRecommendations]);
}

const asEntityIdList = (entities: typeof EntityList) =>
	entities.map((entity) => ({ entityId: entity }));
